import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QuillEditor from '../components/QuillEditor';
import { uploadImageToCloudinary } from '../utils/cloudinaryConfig';
import { getCurrentAdmin, logout, getAuthHeaders } from '../utils/auth';

export const BlogAdmin = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    categories: '',
    tags: '',
    published: false,
    images: []
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const currentAdmin = await getCurrentAdmin();
    if (!currentAdmin) {
      navigate('/login');
      return;
    }
    setAdmin(currentAdmin);
    loadBlogs();
  };

  const loadBlogs = async () => {
    try {
      const response = await fetch('/api/blogs', {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to load blogs');

      const data = await response.json();
      setBlogs(data.blogs || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading blogs:', error);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleContentChange = (content) => {
    setFormData({
      ...formData,
      content
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadError('');

    try {
      const uploadPromises = files.map(async (file) => {
        const caption = prompt(`Enter caption for ${file.name} (optional):`);
        return await uploadImageToCloudinary(file, caption || '');
      });

      const uploadedImages = await Promise.all(uploadPromises);
      setFormData({
        ...formData,
        images: [...formData.images, ...uploadedImages]
      });
    } catch (error) {
      setUploadError('Failed to upload images. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const updateImageCaption = (index, newCaption) => {
    const newImages = [...formData.images];
    newImages[index] = { ...newImages[index], caption: newCaption };
    setFormData({ ...formData, images: newImages });
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const blogData = {
      title: formData.title,
      slug: generateSlug(formData.title),
      content: formData.content,
      excerpt: formData.excerpt,
      categories: formData.categories.split(',').map(c => c.trim()).filter(c => c),
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
      published: formData.published,
      images: formData.images
    };

    try {
      const method = currentBlog ? 'PUT' : 'POST';
      const body = currentBlog ? { ...blogData, id: currentBlog.id } : blogData;

      const response = await fetch('/api/blogs', {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save blog');
      }

      resetForm();
      loadBlogs();
      alert('Blog saved successfully!');
    } catch (error) {
      alert('Error saving blog: ' + error.message);
    }
  };

  const editBlog = (blog) => {
    setCurrentBlog(blog);
    setFormData({
      title: blog.title,
      content: blog.content,
      excerpt: blog.excerpt || '',
      categories: blog.categories ? blog.categories.join(', ') : '',
      tags: blog.tags ? blog.tags.join(', ') : '',
      published: blog.published,
      images: blog.images || []
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    try {
      const response = await fetch(`/api/blogs?id=${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete blog');

      loadBlogs();
    } catch (error) {
      alert('Error deleting blog: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      categories: '',
      tags: '',
      published: false,
      images: []
    });
    setCurrentBlog(null);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-custom-background flex items-center justify-center">
        <p className="text-zinc-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-custom-background py-20 px-10 lg:px-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-grandslang text-[60px] text-charcoal">
              Admin Panel
            </h1>
            <p className="text-zinc-500">Welcome, {admin?.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-6 py-2 border border-charcoal text-charcoal hover:bg-charcoal hover:text-white transition-colors"
          >
            Logout
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-4 mb-8 border-b border-charcoal/20">
          <button
            onClick={() => navigate('/admin')}
            className="px-6 py-3 text-charcoal border-b-2 border-charcoal font-medium"
          >
            Blogs
          </button>
          <button
            onClick={() => navigate('/admin/gallery')}
            className="px-6 py-3 text-charcoal/50 hover:text-charcoal border-b-2 border-transparent hover:border-charcoal/30"
          >
            Gallery
          </button>
          <button
            onClick={() => navigate('/admin/projects')}
            className="px-6 py-3 text-charcoal/50 hover:text-charcoal border-b-2 border-transparent hover:border-charcoal/30"
          >
            Projects
          </button>
        </div>

        {/* Blog Form */}
        <div className="bg-white p-8 mb-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-6 text-charcoal">
            {currentBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
                placeholder="Enter blog title"
              />
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Excerpt (Short Description)
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows="2"
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
                placeholder="Brief description of the blog post"
              />
            </div>

            {/* Content - Quill Editor */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Content *
              </label>
              <QuillEditor
                value={formData.content}
                onChange={handleContentChange}
                placeholder="Write your blog content here..."
              />
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Images
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={uploading}
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
              />
              {uploading && <p className="text-sm text-charcoal mt-2">Uploading images...</p>}
              {uploadError && <p className="text-sm text-red-600 mt-2">{uploadError}</p>}

              {formData.images.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {formData.images.map((image, index) => (
                    <div key={index} className="border border-charcoal/20 p-3">
                      <img
                        src={image.url}
                        alt={image.caption || `Image ${index + 1}`}
                        className="w-full h-40 object-cover mb-2"
                      />
                      <input
                        type="text"
                        value={image.caption}
                        onChange={(e) => updateImageCaption(index, e.target.value)}
                        placeholder="Image caption..."
                        className="w-full p-2 border border-charcoal/30 text-sm mb-2"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Categories */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Categories (comma-separated)
              </label>
              <input
                type="text"
                name="categories"
                value={formData.categories}
                onChange={handleInputChange}
                placeholder="e.g., Technology, Design, Frontend"
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="e.g., react, javascript, css"
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
              />
            </div>

            {/* Published */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleInputChange}
                className="mr-2 w-5 h-5"
              />
              <label className="text-sm font-medium text-charcoal">
                Publish this blog post
              </label>
            </div>

            {/* Buttons */}
            <div className="flex gap-4">
              <button
                type="submit"
                className="px-6 py-3 bg-charcoal text-white hover:bg-charcoal/90 transition-colors"
              >
                {currentBlog ? 'Update Blog' : 'Create Blog'}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 border border-charcoal text-charcoal hover:bg-charcoal/10"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Blog List */}
        <div className="bg-white p-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-6 text-charcoal">
            All Blog Posts ({blogs.length})
          </h2>

          {blogs.length === 0 ? (
            <p className="text-charcoal/70">No blog posts yet. Create your first one above!</p>
          ) : (
            <div className="space-y-4">
              {blogs.map((blog) => (
                <div
                  key={blog.id}
                  className="border border-charcoal/20 p-4 flex justify-between items-start"
                >
                  <div className="flex-1">
                    <h3 className="font-grandslang text-xl text-charcoal mb-2">
                      {blog.title}
                      {!blog.published && (
                        <span className="ml-3 text-sm text-red-600 font-normal">
                          (Draft)
                        </span>
                      )}
                    </h3>
                    {blog.excerpt && (
                      <p className="text-sm text-charcoal/70 mb-2">{blog.excerpt}</p>
                    )}
                    <div className="text-xs text-charcoal/50">
                      Created: {new Date(blog.createdAt).toLocaleDateString()}
                      {blog.categories && blog.categories.length > 0 && (
                        <span className="ml-4">
                          Categories: {blog.categories.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => editBlog(blog)}
                      className="px-4 py-2 bg-charcoal text-white hover:bg-charcoal/90 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(blog.id)}
                      className="px-4 py-2 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
